import { major } from 'semver';
import fg from 'fast-glob';
import { abortIfCancelled, getPackageDotJson } from '../../utils/clack-utils.js';
import clack from '../../utils/clack.js';
import { getVersionBucket } from '../../utils/semver.js';
import type { InstallerOptions } from '../../utils/types.js';
import { IGNORE_PATTERNS } from '../../lib/constants.js';
import { getPackageVersion } from '../../utils/package-json.js';
import chalk from 'chalk';
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as semver from 'semver';

export enum ReactRouterMode {
  V6 = 'v6',
  V7_FRAMEWORK = 'v7-framework',
  V7_DATA = 'v7-data',
  V7_DECLARATIVE = 'v7-declarative',
}

export function getReactRouterVersionBucket(version: string | undefined): string {
  return getVersionBucket(version, 6);
}

async function hasReactRouterConfig({ installDir }: Pick<InstallerOptions, 'installDir'>): Promise<boolean> {
  const configMatches = await fg('**/react-router.config.@(ts|js|tsx|jsx)', {
    dot: true,
    cwd: installDir,
    ignore: IGNORE_PATTERNS,
  });
  return configMatches.length > 0;
}

async function hasCreateBrowserRouter({ installDir }: Pick<InstallerOptions, 'installDir'>): Promise<boolean> {
  const sourceFiles = await fg('**/*.@(ts|tsx|js|jsx)', {
    dot: true,
    cwd: installDir,
    ignore: IGNORE_PATTERNS,
  });

  for (const file of sourceFiles) {
    try {
      const filePath = path.join(installDir, file);
      const content = fs.readFileSync(filePath, 'utf-8');
      if (content.includes('createBrowserRouter')) {
        return true;
      }
    } catch {
      continue;
    }
  }
  return false;
}

async function hasDeclarativeRouter({ installDir }: Pick<InstallerOptions, 'installDir'>): Promise<boolean> {
  const sourceFiles = await fg('**/*.@(ts|tsx|js|jsx)', {
    dot: true,
    cwd: installDir,
    ignore: IGNORE_PATTERNS,
  });

  for (const file of sourceFiles) {
    try {
      const filePath = path.join(installDir, file);
      const content = fs.readFileSync(filePath, 'utf-8');
      if (
        content.includes('<BrowserRouter') ||
        (content.includes('BrowserRouter') &&
          (content.includes('from "react-router-dom"') || content.includes("from 'react-router-dom'")))
      ) {
        return true;
      }
    } catch {
      continue;
    }
  }
  return false;
}

export async function getReactRouterMode(options: InstallerOptions): Promise<ReactRouterMode> {
  const { installDir } = options;

  const packageJson = await getPackageDotJson(options);
  const reactRouterVersion =
    getPackageVersion('react-router-dom', packageJson) || getPackageVersion('react-router', packageJson);

  if (!reactRouterVersion) {
    clack.log.info(`Learn more about React Router modes: ${chalk.cyan('https://reactrouter.com/start/modes')}`);
    const result: ReactRouterMode = await abortIfCancelled(
      clack.select({
        message: 'What React Router version and mode are you using?',
        options: [
          { label: 'React Router v6', value: ReactRouterMode.V6 },
          { label: 'React Router v7 - Framework mode', value: ReactRouterMode.V7_FRAMEWORK },
          { label: 'React Router v7 - Data mode', value: ReactRouterMode.V7_DATA },
          { label: 'React Router v7 - Declarative mode', value: ReactRouterMode.V7_DECLARATIVE },
        ],
      }),
      'react-router',
    );
    return result;
  }

  const coercedVersion = semver.coerce(reactRouterVersion);
  const majorVersion = coercedVersion ? major(coercedVersion) : null;

  if (majorVersion === 6) {
    clack.log.info('Detected React Router v6');
    return ReactRouterMode.V6;
  }

  if (majorVersion === 7) {
    const hasConfig = await hasReactRouterConfig({ installDir });
    if (hasConfig) {
      clack.log.info('Detected React Router v7 - Framework mode');
      return ReactRouterMode.V7_FRAMEWORK;
    }

    const hasDataMode = await hasCreateBrowserRouter({ installDir });
    if (hasDataMode) {
      clack.log.info('Detected React Router v7 - Data mode');
      return ReactRouterMode.V7_DATA;
    }

    const hasDeclarative = await hasDeclarativeRouter({ installDir });
    if (hasDeclarative) {
      clack.log.info('Detected React Router v7 - Declarative mode');
      return ReactRouterMode.V7_DECLARATIVE;
    }

    clack.log.info(`Learn more about React Router modes: ${chalk.cyan('https://reactrouter.com/start/modes')}`);
    const result: ReactRouterMode = await abortIfCancelled(
      clack.select({
        message: 'What React Router v7 mode are you using?',
        options: [
          { label: 'Framework mode', value: ReactRouterMode.V7_FRAMEWORK },
          { label: 'Data mode', value: ReactRouterMode.V7_DATA },
          { label: 'Declarative mode', value: ReactRouterMode.V7_DECLARATIVE },
        ],
      }),
      'react-router',
    );
    return result;
  }

  clack.log.info(`Learn more about React Router modes: ${chalk.cyan('https://reactrouter.com/start/modes')}`);
  const result: ReactRouterMode = await abortIfCancelled(
    clack.select({
      message: 'What React Router version and mode are you using?',
      options: [
        { label: 'React Router v6', value: ReactRouterMode.V6 },
        { label: 'React Router v7 - Framework mode', value: ReactRouterMode.V7_FRAMEWORK },
        { label: 'React Router v7 - Data mode', value: ReactRouterMode.V7_DATA },
        { label: 'React Router v7 - Declarative mode', value: ReactRouterMode.V7_DECLARATIVE },
      ],
    }),
    'react-router',
  );
  return result;
}

export function getReactRouterModeName(mode: ReactRouterMode): string {
  switch (mode) {
    case ReactRouterMode.V6:
      return 'v6';
    case ReactRouterMode.V7_FRAMEWORK:
      return 'v7 Framework mode';
    case ReactRouterMode.V7_DATA:
      return 'v7 Data mode';
    case ReactRouterMode.V7_DECLARATIVE:
      return 'v7 Declarative mode';
  }
}
